import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, AvatarGroup, Box, Button, Paper, Stack, Typography, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Calendar, Clock } from "@phosphor-icons/react";
import { format, isPast, isBefore, isToday, isTomorrow, isYesterday, subMinutes, isSameWeek } from "date-fns";
import { BILLING_TYPES } from "../../../constants";
import getAvatarInitials from "../../../utils/getAvatarInitials";
import EmptyNextAppointment from "./EmptyNextAppointment";
import { Appointment } from "api/types/appointments";
import { useJoinAppointment, isJoinable } from "hooks/useJoinAppointment";
import { BILLING_TYPE_I18N_KEYS } from "../utils";
import { getDateFnsLocale } from "utils/getDateFnsLocale";
import { formatLocalizedDate } from "utils/formatLocalizedDate";

interface NextAppointmentCardProps {
  appointmentData: Appointment;
  showJoinButton: boolean;
}

const NextAppointmentCard: React.FC<NextAppointmentCardProps> = ({
  appointmentData,
  showJoinButton,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["dashboard"]);
  const { joinAppointment } = useJoinAppointment();

  if (!appointmentData || !appointmentData?.scheduled_date) {
    return <EmptyNextAppointment />;
  }

  const {
    id,
    billing_type,
    call_duration,
    scheduled_date,
    video_key,
    off_platform,
    client_name,
    client_avatar,
    clinician_name,
    clinician_avatar,
    secondary_clinician_name,
    secondary_clinician_avatar,
  } = appointmentData as Appointment & { billing_type: keyof typeof BILLING_TYPES };
  const start = new Date(scheduled_date);
  const end = new Date(start.getTime() + call_duration * 60000);
  // if start date is in the current week, show relative date if not, show the date like "Monday, July 7th"
  const startDate = isSameWeek(start, new Date())
    ? isToday(start)
      ? t("dashboard:parent.nextAppointment.today")
      : isTomorrow(start)
        ? t("dashboard:parent.nextAppointment.tomorrow")
        : isYesterday(start)
          ? t("dashboard:parent.nextAppointment.yesterday")
          : format(start, "EEEE", { locale: getDateFnsLocale() })
    : formatLocalizedDate(start, { en: "EEEE, MMMM do", es: "EEEE, d 'de' MMMM" });

  const billingTypeKey = BILLING_TYPE_I18N_KEYS[billing_type as keyof typeof BILLING_TYPES];
  const serviceName = billingTypeKey
    ? t(`dashboard:parent.nextAppointment.billingTypes.${billingTypeKey}`)
    : BILLING_TYPES[billing_type as keyof typeof BILLING_TYPES];
  const isBeforeStart = isBefore(new Date(), start);

  const isJoinButtonDisabled =
    !video_key || isPast(end) || isBefore(new Date(), subMinutes(start, 15));
  const showManageAppointment = [
    "DIRECT_THERAPY",
    "CAREGIVER_MEDIATED",
    "CAREGIVER_TRAINING",
  ].includes(appointmentData?.billing_type);

  const eventAttendees = [
    {
      id: "attendee-1",
      name: client_name,
      avatar: client_avatar ?? null,
    },
    {
      id: "attendee-2",
      name: clinician_name,
      avatar: clinician_avatar ?? null,
    },
    ...(secondary_clinician_name
      ? [
          {
            id: "attendee-3",
            name: secondary_clinician_name,
            avatar: secondary_clinician_avatar ?? null,
          },
        ]
      : []),
  ];

  return (
    <Paper
      component="section"
      elevation={0}
      sx={{
        padding: "20px",
        borderRadius: "16px",
        width: "100%",
        backgroundColor: "#F6F7F8",
      }}
    >
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" px={0} py={0}>
          <AvatarGroup max={3} sx={{ justifyContent: "flex-end" }}>
            {eventAttendees.map((attendee) => (
              <Tooltip key={attendee.id} title={attendee.name} arrow placement="top">
                <Avatar
                  alt={attendee.name}
                  src={attendee.avatar ?? undefined}
                  sx={{ width: 40, height: 40, fontSize: "12px" }}
                  data-testid={`avatar-item-${attendee.id}`}
                >
                  {getAvatarInitials(attendee.name)}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        </Box>

        <Typography component="h3" variant="h2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
          {serviceName}
        </Typography>

        <Stack
          direction="row"
          columnGap={7}
          rowGap={2}
          mb={7}
          alignItems="center"
          flexWrap={"wrap"}
          textAlign="center"
        >
          <Typography
            component="p"
            variant="body1"
            color="text.secondary"
            sx={{ display: "flex", gap: 3, alignItems: "center" }}
          >
            <span style={{ verticalAlign: "bottom", lineHeight: "1" }}>
              <Clock size={20} weight="duotone" style={{ color: theme.palette.grey[600] }} />
            </span>{" "}
            {format(start, "h:mm")} - {format(end, "p")}
          </Typography>

          <Typography
            component="p"
            variant="body1"
            color="text.secondary"
            textTransform="capitalize"
            sx={{ display: "flex", gap: 3, alignItems: "center" }}
          >
            <span style={{ verticalAlign: "middle", lineHeight: "1" }}>
              <Calendar size={20} weight="duotone" style={{ color: theme.palette.grey[600] }} />
            </span>{" "}
            {startDate}
          </Typography>
        </Stack>

        {showJoinButton && isJoinable(appointmentData) && (
          <Button
            variant="contained"
            disabled={isJoinButtonDisabled}
            color="primary"
            size="large"
            onClick={() => joinAppointment({ offPlatform: off_platform, videoKey: video_key })}
            sx={{
              width: "100%",
            }}
          >
            {t("dashboard:parent.nextAppointment.joinButton")}
          </Button>
        )}

        {isBeforeStart && showManageAppointment && (
          <Typography
            component="p"
            variant="body2"
            sx={{ mt: 5, textAlign: "center" }}
            color="primary.main"
          >
            <Link
              to={`/rescheduling-session/${id}?utm_source=site&utm_medium=organic&utm_campaign=client_rescheduling`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {t("dashboard:parent.nextAppointment.manageAppointment")}
            </Link>
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default NextAppointmentCard;
